/**
 * Invite a Friend — /(app)/settings/invite
 *
 * Form for inviting someone who doesn't yet have a WarCarp account.
 * The inviter enters an email and an optional personal message; the
 * API sends a branded email with the inviter's name + email + message
 * and a link to register.
 */

import { COMPACT_HEADER_STYLE } from '../../../lib/gameChrome'
import React, { useState } from 'react'
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  useWindowDimensions,
} from 'react-native'
import { useRouter } from 'expo-router'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useOrientation } from '../../../hooks/useOrientation'
import { useTheme } from '../../../hooks/useTheme'
import { useAppInvite } from '../../../hooks/useAppInvite'
import { HttpError } from '../../../lib/api'
import { KeyboardAwareScrollView } from '../../../components/ui/KeyboardAwareScrollView'
import { goBackOr } from '../../../lib/goBack'

const MAX_MESSAGE_LENGTH = 1000
// Same loose-but-real email regex used elsewhere in the app —
// catches obvious typos client-side, server still validates.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default function InviteScreen() {
  const router = useRouter()
  const { theme } = useTheme()
  const placeholderHex = theme === 'dark' ? '#8a8c6e' : '#6b5b3e'
  const { width: winWidth } = useWindowDimensions()
  const { isLandscape } = useOrientation()
  const contentWidth = isLandscape ? Math.min(560, winWidth * 0.6) : undefined

  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [sent, setSent] = useState(false)
  const [sentTo, setSentTo] = useState<string | null>(null)
  const invite = useAppInvite()

  const trimmedEmail = email.trim().toLowerCase()
  const emailValid = EMAIL_RE.test(trimmedEmail)
  const canSubmit = emailValid && !invite.isPending && !sent

  async function handleSubmit() {
    if (!canSubmit) return
    try {
      const trimmedMsg = message.trim()
      await invite.mutateAsync({
        email: trimmedEmail,
        ...(trimmedMsg.length > 0 ? { message: trimmedMsg } : {}),
      })
      setSent(true)
      setSentTo(trimmedEmail)
    } catch {
      // Error surfaced inline via invite.isError / invite.error below.
    }
  }

  function inviteAnother() {
    setEmail('')
    setMessage('')
    setSent(false)
    setSentTo(null)
    invite.reset()
  }

  // Specialise the error copy when the API returns 409 (email already
  // has a WarCarp account) — the generic "couldn't send" message
  // would be misleading there.
  const errorMessage = (() => {
    if (!invite.isError) return null
    const err = invite.error
    if (err instanceof HttpError && err.status === 409) {
      return 'That email already has a WarCarp account. Send them a game invite instead!'
    }
    return "Couldn't send the invite — please try again."
  })()

  return (
    <SafeAreaView className="flex-1 bg-canvas" edges={['top']}>
      <View className="flex-row items-center px-4 border-b border-olive" style={COMPACT_HEADER_STYLE}>
        <TouchableOpacity
          onPress={() => goBackOr(router, '/(app)/settings')}
          accessibilityLabel="Back"
          accessibilityRole="button"
          className="mr-3 p-1"
        >
          <Text className="text-blood text-base font-typed">← Back</Text>
        </TouchableOpacity>
        <Text className="text-ink font-stencil text-xl flex-1 uppercase">Invite a Friend</Text>
      </View>

      <KeyboardAwareScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{
          padding: 16,
          gap: 16,
          paddingBottom: 32,
          alignItems: isLandscape ? 'center' : undefined,
        }}
      >
          <View style={isLandscape ? { width: contentWidth, gap: 16 } : { width: '100%', gap: 16 }}>
          {sent ? (
            <View className="bg-paper border-2 border-olive rounded-xl p-4 gap-3 items-center">
              <Text className="font-stencil text-ink text-base uppercase tracking-widest">
                Invite sent.
              </Text>
              <Text className="font-typed text-ink-soft text-sm text-center">
                We emailed an invite to{' '}
                <Text className="text-ink">{sentTo}</Text>. Replies to that
                email come back to you directly.
              </Text>
              <TouchableOpacity
                onPress={inviteAnother}
                accessibilityLabel="Invite another friend"
                accessibilityRole="button"
                className="bg-paper-up border border-olive rounded-xl px-4 py-2 mt-2"
                style={{ minHeight: 40 }}
              >
                <Text className="text-ink font-stencil uppercase text-sm">
                  Invite another
                </Text>
              </TouchableOpacity>
            </View>
          ) : (
            <>
              <View className="bg-paper border-2 border-olive rounded-xl p-4 gap-2">
                <Text className="font-typed text-ink text-sm leading-6">
                  Know someone that would enjoy WarCarp? Drop their email
                  and we'll send them a personal invite.
                </Text>
                <Text className="font-typed text-ink-soft text-xs italic">
                  Your account name and email are included so they know
                  who invited them and can reply directly.
                </Text>
              </View>

              {/* Email */}
              <View className="gap-2">
                <Text className="font-stencil text-[10px] uppercase tracking-widest text-olive-up">
                  Their email
                </Text>
                <TextInput
                  value={email}
                  onChangeText={setEmail}
                  placeholder="friend@example.com"
                  placeholderTextColor={placeholderHex}
                  autoCapitalize="none"
                  autoCorrect={false}
                  keyboardType="email-address"
                  accessibilityLabel="Friend's email"
                  className="bg-paper border-2 border-olive rounded-xl px-3 py-3 text-ink font-typed text-base"
                  style={{ minHeight: 44 }}
                />
                {email.trim().length > 0 && !emailValid && (
                  <Text className="font-typed text-blood text-xs italic">
                    // that doesn't look like a valid email
                  </Text>
                )}
              </View>

              {/* Message */}
              <View className="gap-2">
                <View className="flex-row items-baseline justify-between">
                  <Text className="font-stencil text-[10px] uppercase tracking-widest text-olive-up">
                    Personal message (optional)
                  </Text>
                  <Text className="font-typed text-[10px] text-ink-soft">
                    {message.length}/{MAX_MESSAGE_LENGTH}
                  </Text>
                </View>
                <TextInput
                  value={message}
                  onChangeText={(t) => setMessage(t.slice(0, MAX_MESSAGE_LENGTH))}
                  placeholder="Say hi — they'll see this in the email."
                  placeholderTextColor={placeholderHex}
                  multiline
                  textAlignVertical="top"
                  accessibilityLabel="Personal message"
                  className="bg-paper border-2 border-olive rounded-xl px-3 py-3 text-ink font-typed text-base"
                  style={{ minHeight: 120 }}
                />
              </View>

              {errorMessage && (
                <Text className="font-typed text-blood text-sm">
                  {errorMessage}
                </Text>
              )}

              <TouchableOpacity
                onPress={handleSubmit}
                disabled={!canSubmit}
                accessibilityLabel="Send invite"
                accessibilityRole="button"
                className="bg-blood rounded-xl px-4 py-3 items-center justify-center flex-row gap-2"
                style={{ minHeight: 48, opacity: canSubmit ? 1 : 0.4 }}
              >
                {invite.isPending && <ActivityIndicator size="small" color="white" />}
                <Text className="text-white font-stencil uppercase">Send Invite</Text>
              </TouchableOpacity>
            </>
          )}
          </View>
      </KeyboardAwareScrollView>
    </SafeAreaView>
  )
}
